/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in diagram-tooltip.css. */
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react"
import { createPortal } from "react-dom"

import "./diagram-tooltip.css"

interface ShownTip {
  title: string
  body: string
  rect: DOMRect
}

const GAP = 10
const EDGE = 12

function tipTarget(node: EventTarget | null, root: HTMLElement): Element | null {
  if (!(node instanceof Element)) return null
  const target = node.closest("[data-tip]")
  return target && root.contains(target) ? target : null
}

function readTip(target: Element): ShownTip {
  return {
    title: target.getAttribute("data-tip-title") ?? "",
    body: target.getAttribute("data-tip") ?? "",
    rect: target.getBoundingClientRect(),
  }
}

/** Whether a part can still carry its tip: attached, still marked, and showing. */
function isLive(target: Element): boolean {
  if (!target.isConnected || !target.hasAttribute("data-tip")) return false
  const style = getComputedStyle(target)
  return style.pointerEvents !== "none" && style.visibility !== "hidden" && style.opacity !== "0"
}

/**
 * Hover (or, on touch screens, tap) explanations for the parts of an article
 * diagram. Mark a part with `data-tip` (and an optional `data-tip-title`); one
 * set of listeners on the figure covers every part, including ones the
 * animation re-renders each frame. The tips add detail the captions and the
 * figure's label already cover, so they stay pointer-only.
 */
export function DiagramTooltip({ rootRef }: { rootRef: RefObject<HTMLElement | null> }) {
  const [shown, setShown] = useState<ShownTip | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    let current: Element | null = null
    let shownRect: DOMRect | null = null
    let pointer: { x: number; y: number } | null = null
    let frame = 0

    const show = (target: Element | null) => {
      current = target
      const tip = target ? readTip(target) : null
      shownRect = tip?.rect ?? null
      setShown(tip)
      cancelAnimationFrame(frame)
      if (target) frame = requestAnimationFrame(watch)
    }

    // The animation can hide, move or unmark the part under a still pointer,
    // so while a tip is open re-check each frame what the pointer (or the
    // tapped part) is actually on, and follow, move or close the tip to match.
    const watch = () => {
      if (!current) return
      const hit = pointer
        ? tipTarget(document.elementFromPoint(pointer.x, pointer.y), root)
        : isLive(current)
          ? current
          : null
      if (hit !== current || !isLive(current)) {
        show(hit && isLive(hit) ? hit : null)
        return
      }
      // The part can move under the tip (the Mirror propagation camera pans
      // and zooms), so follow it rather than leaving the tip where it opened.
      const rect = current.getBoundingClientRect()
      if (
        shownRect &&
        (Math.abs(rect.left - shownRect.left) > 0.5 ||
          Math.abs(rect.top - shownRect.top) > 0.5 ||
          Math.abs(rect.width - shownRect.width) > 0.5 ||
          Math.abs(rect.height - shownRect.height) > 0.5)
      ) {
        shownRect = rect
        setShown((tip) => (tip ? { ...tip, rect } : tip))
      }
      frame = requestAnimationFrame(watch)
    }

    const onOver = (event: PointerEvent) => {
      if (event.pointerType === "touch") return
      pointer = { x: event.clientX, y: event.clientY }
      const target = tipTarget(event.target, root)
      if (target !== current) show(target)
    }
    const onMove = (event: PointerEvent) => {
      if (event.pointerType === "touch") return
      pointer = { x: event.clientX, y: event.clientY }
      // A scroll or resize closes the tip without the pointer leaving its
      // part, so reopen it as soon as the pointer moves again.
      if (!current) {
        const target = tipTarget(event.target, root)
        if (target && isLive(target)) show(target)
      }
    }
    const onLeave = (event: PointerEvent) => {
      if (event.pointerType === "touch") return
      pointer = null
      show(null)
    }
    const onTap = (event: PointerEvent) => {
      if (event.pointerType !== "touch") return
      pointer = null
      const target = tipTarget(event.target, root)
      show(target && target !== current ? target : null)
    }
    const onOutsideTap = (event: PointerEvent) => {
      if (current && !root.contains(event.target as Node)) show(null)
    }
    const hide = () => show(null)

    root.addEventListener("pointerover", onOver)
    root.addEventListener("pointermove", onMove)
    root.addEventListener("pointerleave", onLeave)
    root.addEventListener("pointerup", onTap)
    document.addEventListener("pointerdown", onOutsideTap)
    window.addEventListener("scroll", hide, { passive: true })
    window.addEventListener("resize", hide)
    return () => {
      cancelAnimationFrame(frame)
      root.removeEventListener("pointerover", onOver)
      root.removeEventListener("pointermove", onMove)
      root.removeEventListener("pointerleave", onLeave)
      root.removeEventListener("pointerup", onTap)
      document.removeEventListener("pointerdown", onOutsideTap)
      window.removeEventListener("scroll", hide)
      window.removeEventListener("resize", hide)
    }
  }, [rootRef])

  // Place the panel once it can be measured: below the part when it fits,
  // otherwise above, otherwise on whichever side has more room, and always
  // clamped inside the viewport so it never opens off screen.
  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!shown || !panel) return
    const { width, height } = panel.getBoundingClientRect()
    const { rect } = shown
    const roomBelow = window.innerHeight - rect.bottom - GAP - EDGE
    const roomAbove = rect.top - GAP - EDGE
    const below = height <= roomBelow || (height > roomAbove && roomBelow >= roomAbove)
    const top = below ? rect.bottom + GAP : rect.top - GAP - height
    const centre = rect.left + rect.width / 2
    panel.style.top = `${Math.min(Math.max(top, EDGE), window.innerHeight - height - EDGE)}px`
    panel.style.left = `${Math.min(Math.max(centre - width / 2, EDGE), window.innerWidth - width - EDGE)}px`
    panel.style.visibility = "visible"
  }, [shown])

  if (!shown || typeof document === "undefined") return null

  return createPortal(
    <div ref={panelRef} className="diagram-tooltip" role="tooltip" style={{ visibility: "hidden" }}>
      {shown.title ? <b>{shown.title}</b> : null}
      <span>{shown.body}</span>
    </div>,
    document.body,
  )
}
